import { useTranslation } from 'react-i18next';
import { UserRoles } from '../../../Constants/userRoles';
import useUserAccess from '../../../Hooks/useUserAccess';
import helpers from '../../../Utils';
import { Dropdown as UntypedDropdown } from '../../FormComponents/Dropdown';
import { ReadOnlyField as UntypedReadOnlyField } from '../../FormComponents/Input/Input';
import UntypedUserSearchNew from '../../Search/UserSearchNew';
import type { ModifySubscriptionForm, ProductOffer, SalesPerson, Subscription } from './ConfirmSubscriptionChange.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UntypedComponent = any;

const Dropdown: UntypedComponent = UntypedDropdown;
const ReadOnlyField: UntypedComponent = UntypedReadOnlyField;
const UserSearchNew: UntypedComponent = UntypedUserSearchNew;

interface ChangeItemProps {
    label: string;
    oldValue: string | null | undefined;
    newValue: string | null | undefined;
}

const ChangeItem = ({ label, oldValue, newValue }: ChangeItemProps) => (
    <div>
        <label>{label}</label>
        <div className="d-flex gap-12 align-items-center">
            <div className="text-warning w-100"><ReadOnlyField value={oldValue} /></div>
            <span>→</span>
            <div className="text-success w-100"><ReadOnlyField value={newValue} /></div>
        </div>
    </div>
);

interface Props {
    subscription: Subscription;
    form: ModifySubscriptionForm;
    offer: ProductOffer;
    orderType: string;
    onOrderTypeChange: (orderType: string) => void;
    onSalesPersonChange: (salesPerson: SalesPerson | null) => void;
}

const ConfirmSubscriptionChangeForm = ({ subscription, form, offer, orderType, onOrderTypeChange, onSalesPersonChange }: Props) => {
    const { t } = useTranslation();
    const { hasReadWriteAccess } = useUserAccess();

    const isTierChanged = subscription.tier.id !== form.data.tierId;
    const isPlanChanged = subscription.plan.id !== form.data.planId;
    const newTierName = offer.tiers.find(tier => tier.id === form.data.tierId)?.name;
    const newPlanName = offer.plans.find(plan => plan.id === form.data.planId)?.name;

    const orderTypeOptions = [
        { id: 'ordinary', label: t('confirmordermodal.ordinary') },
        {
            id: 'other',
            label: t('confirmordermodal.orderforother'),
            disabled: !Boolean(hasReadWriteAccess(UserRoles.OrderForOther))
        },
        { id: 'partner', label: t('confirmordermodal.partner') },
        { id: 'web', label: t('confirmordermodal.web') },
        { id: 'onlineSales', label: t('confirmordermodal.onlineorder') }
    ];

    return (
        <div className="d-grid grid-cols-1 gap-16">
            <div className="d-grid grid-cols-1 gap-12">
                {isTierChanged && <ChangeItem label={t('common.tier')} oldValue={subscription.tier.name} newValue={newTierName} />}
                {isPlanChanged && <ChangeItem label={t('common.plan')} oldValue={subscription.plan.name} newValue={newPlanName} />}
                {Boolean(form.data.endDate) && <ChangeItem
                    label={t('confirmsubscriptionchange.newEndDate')}
                    oldValue={helpers.formatDate(subscription.bindingPeriod.endDate)}
                    newValue={form.data.endDate} />}
            </div>
            <div className="form-group mb-0">
                <Dropdown
                    options={orderTypeOptions}
                    value={orderType}
                    onChange={onOrderTypeChange}
                    property="label"
                    label={t('confirmordermodal.orderType')}
                    disabledOptionKey="disabled" />
            </div>
            {Boolean(hasReadWriteAccess(UserRoles.OrderForOther)) && orderType === 'other' && <div className="form-group mb-0">
                <label>{t('confirmsubscriptionchange.seller')}</label>
                <UserSearchNew onChange={onSalesPersonChange} />
                <small>
                    {t('confirmsubscriptionchange.keepCurrentSeller')}
                </small>
            </div>}
        </div>
    );
};

export default ConfirmSubscriptionChangeForm;
